import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { UrlNavigationService } from '../url-navigation/url-navigation.service';
import { ENTITY_MODE } from '../../typings/types/entity-mode.types';
import { ENTITY_TYPE } from '../../typings/types/entity-type.types';
import { OpGlobalFilterService, SUB_LOCATION_FILTER_KEY } from './op-global-filter.service';

/**
 * Keeps the global uber filter in step with the sub-location in the URL. The
 * entity picker writes its selection to the URL, and a shared link carries one
 * too, so the route is the single source of truth for both paths. Applies only
 * in shelter single-entity mode — the only context with sub-locations.
 */
@Injectable({ providedIn: 'root' })
export class SubLocationFilterSyncService {
  private isWatching = false;

  constructor (
    private router: Router,
    private opGlobalFilter: OpGlobalFilterService,
  ) {}

  /**
   * Sync immediately — so data resolvers that run after this see the filter —
   * and again on every completed navigation, which covers picker selections
   * and query-param-only quick-switches alike. Idempotent, so it is safe to
   * call on every OP entry.
   */
  public start (): void {
    if (this.isWatching) return;
    this.isWatching = true;

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.syncFromUrl());

    this.syncFromUrl();
  }

  private syncFromUrl (): void {
    const { entityType, entityMode, subEntityId } = UrlNavigationService.getOpContext(this.getLeafSnapshot());
    const isShelterSingleEntity = entityType === ENTITY_TYPE.SHELTER && entityMode === ENTITY_MODE.SINGLE_ENTITY;

    if (!isShelterSingleEntity || !subEntityId) {
      this.opGlobalFilter.removeFilter(SUB_LOCATION_FILTER_KEY);
      return;
    }

    this.opGlobalFilter.setFilter(SUB_LOCATION_FILTER_KEY, [subEntityId]);
  }

  // Query params live on the leaf with paramsInheritanceStrategy, so read the deepest snapshot.
  private getLeafSnapshot (): ActivatedRouteSnapshot {
    let snapshot = this.router.routerState.snapshot.root;
    while (snapshot.firstChild) snapshot = snapshot.firstChild;
    return snapshot;
  }
}
